/**
 * Manual Markup Editor Toolbar
 * Provides tools for manually editing document annotations
 */

import React from 'react';
import { 
  MousePointer2, 
  Square, 
  SquareDashed, 
  ArrowRight, 
  Type, 
  Trash2,
  Undo2,
  Redo2,
  RotateCcw,
  Check,
  X
} from 'lucide-react';
import { Button } from '../../components/ui/button';

// Tool types
export const TOOL_TYPES = {
  SELECT: 'select',
  RECTANGLE: 'rectangle',
  DASHED_RECTANGLE: 'dashed_rectangle',
  ARROW: 'arrow',
  TEXT_NOTE: 'text_note',
  DELETE: 'delete'
};

// Font presets (controlled options only)
export const FONT_PRESETS = {
  LEGAL_SANS: {
    id: 'legal_sans',
    label: 'Legal Sans',
    fontFamily: 'system-ui, -apple-system, sans-serif',
    fontWeight: '700'
  },
  REVIEW_SERIF: {
    id: 'review_serif',
    label: 'Review Serif',
    fontFamily: 'Georgia, "Times New Roman", serif',
    fontWeight: '600'
  },
  MARKER_PRINT: {
    id: 'marker_print',
    label: 'Marker Print',
    fontFamily: '"Comic Sans MS", "Marker Felt", cursive',
    fontWeight: '600'
  }
};

// Size presets (fixed options only)
export const SIZE_PRESETS = {
  SMALL: { id: 'small', label: 'Small', fontSize: 11 },
  STANDARD: { id: 'standard', label: 'Standard', fontSize: 13 },
  LARGE: { id: 'large', label: 'Large', fontSize: 16 }
};

// Severity options
export const SEVERITY_OPTIONS = [
  { id: 'high', label: 'High', color: '#ef4444' },
  { id: 'medium', label: 'Medium', color: '#f59e0b' },
  { id: 'low', label: 'Low', color: '#10b981' }
];

const MarkupEditorToolbar = ({
  activeTool,
  setActiveTool,
  canUndo,
  canRedo,
  onUndo,
  onRedo,
  onResetToAI,
  selectedAnnotation,
  onUpdateAnnotation,
  onDeleteSelected,
  isEditMode,
  setIsEditMode,
  tierPermissions
}) => {
  const tools = [
    { id: TOOL_TYPES.SELECT, icon: MousePointer2, label: 'Select', shortcut: 'V' },
    { id: TOOL_TYPES.RECTANGLE, icon: Square, label: 'Rectangle', shortcut: 'R' },
    { id: TOOL_TYPES.DASHED_RECTANGLE, icon: SquareDashed, label: 'Dashed Rect', shortcut: 'D' },
    { id: TOOL_TYPES.ARROW, icon: ArrowRight, label: 'Arrow', shortcut: 'A' },
    { id: TOOL_TYPES.TEXT_NOTE, icon: Type, label: 'Text Note', shortcut: 'T' },
    { id: TOOL_TYPES.DELETE, icon: Trash2, label: 'Delete', shortcut: 'Del' }
  ];

  // Check if user has manual editing permissions
  const canManualEdit = tierPermissions?.can_edit_findings !== false;
  const canAddManual = tierPermissions?.can_add_manual_findings !== false;

  return (
    <div className="flex flex-col gap-2 p-2 bg-stone-800 rounded-lg shadow-lg" data-testid="markup-editor-toolbar">
      {/* Edit Mode Toggle */}
      <div className="flex items-center justify-between px-2 py-1 border-b border-slate-700">
        <span className="text-xs text-slate-400 font-medium">Edit Mode</span>
        <Button
          variant={isEditMode ? "default" : "outline"}
          size="sm"
          onClick={() => setIsEditMode(!isEditMode)}
          className={`h-6 px-2 text-xs ${isEditMode ? 'bg-emerald-600 hover:bg-emerald-700' : ''}`}
          data-testid="edit-mode-toggle"
        >
          {isEditMode ? 'ON' : 'OFF'}
        </Button>
      </div>

      {/* Main Tools */}
      {isEditMode && (
        <>
          <div className="flex flex-wrap gap-1 p-1">
            {tools.map((tool) => {
              const Icon = tool.icon;
              const isDisabled = !canManualEdit && tool.id !== TOOL_TYPES.SELECT;
              const isDrawTool = [TOOL_TYPES.RECTANGLE, TOOL_TYPES.DASHED_RECTANGLE, TOOL_TYPES.ARROW, TOOL_TYPES.TEXT_NOTE].includes(tool.id);
              
              if (isDrawTool && !canAddManual) return null;
              
              return (
                <Button
                  key={tool.id}
                  variant={activeTool === tool.id ? "default" : "ghost"}
                  size="sm"
                  onClick={() => setActiveTool(tool.id)}
                  disabled={isDisabled}
                  className={`h-8 w-8 p-0 ${activeTool === tool.id ? 'bg-emerald-600 text-white' : 'text-slate-300 hover:text-white hover:bg-slate-700'}`}
                  title={`${tool.label} (${tool.shortcut})`}
                  data-testid={`tool-${tool.id}`}
                >
                  <Icon className="w-4 h-4" />
                </Button>
              );
            })}
          </div>

          {/* Undo/Redo/Reset */}
          <div className="flex gap-1 p-1 border-t border-slate-700">
            <Button
              variant="ghost"
              size="sm"
              onClick={onUndo}
              disabled={!canUndo}
              className="h-7 px-2 text-slate-300 hover:text-white hover:bg-slate-700 disabled:opacity-40"
              title="Undo (Ctrl+Z)"
              data-testid="undo-btn"
            >
              <Undo2 className="w-3.5 h-3.5 mr-1" />
              <span className="text-xs">Undo</span>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={onRedo}
              disabled={!canRedo}
              className="h-7 px-2 text-slate-300 hover:text-white hover:bg-slate-700 disabled:opacity-40"
              title="Redo (Ctrl+Shift+Z)"
              data-testid="redo-btn"
            >
              <Redo2 className="w-3.5 h-3.5 mr-1" />
              <span className="text-xs">Redo</span>
            </Button>
            <Button
              variant="ghost"
              size="sm"
              onClick={onResetToAI}
              className="h-7 px-2 text-amber-400 hover:text-amber-300 hover:bg-slate-700"
              title="Reset to AI Suggestions"
              data-testid="reset-ai-btn"
            >
              <RotateCcw className="w-3.5 h-3.5 mr-1" />
              <span className="text-xs">Reset</span>
            </Button>
          </div>
        </>
      )}

      {/* Selected Annotation Editor */}
      {isEditMode && selectedAnnotation && canManualEdit && (
        <AnnotationEditor
          annotation={selectedAnnotation}
          onUpdate={onUpdateAnnotation}
          onDelete={onDeleteSelected}
        />
      )}
    </div>
  );
};

/**
 * Annotation Editor Panel
 * Shows when an annotation is selected for editing
 */
const AnnotationEditor = ({ annotation, onUpdate, onDelete }) => {
  const [labelText, setLabelText] = React.useState(annotation.label_text || '');
  const [fontPreset, setFontPreset] = React.useState(annotation.label_font || 'legal_sans');
  const [lawTags, setLawTags] = React.useState((annotation.law_references || []).join(', '));

  React.useEffect(() => {
    setLabelText(annotation.label_text || '');
    setFontPreset(annotation.label_font || 'legal_sans');
    setLawTags((annotation.law_references || []).join(', '));
  }, [annotation]);

  const handleSave = () => {
    onUpdate({
      ...annotation,
      label_text: labelText,
      label_font: fontPreset,
      law_references: lawTags.split(',').map(t => t.trim()).filter(Boolean),
      edited_by_user: true,
      edit_type: 'relabeled',
      updated_at: new Date().toISOString()
    });
  };

  return (
    <div className="p-2 border-t border-slate-700 space-y-2" data-testid="annotation-editor">
      <div className="flex items-center justify-between">
        <span className="text-xs text-slate-400 font-medium">Edit Annotation</span>
        <span className={`text-xs px-1.5 py-0.5 rounded ${annotation.source === 'ai' ? 'bg-emerald-500/20 text-emerald-300' : 'bg-amber-500/20 text-amber-300'}`}>
          {annotation.source === 'ai' ? 'AI' : 'Manual'}
        </span>
      </div>

      {/* Label Text */}
      <div>
        <label className="text-xs text-slate-500 block mb-1">Label Text</label>
        <input
          type="text"
          value={labelText}
          onChange={(e) => setLabelText(e.target.value)}
          className="w-full px-2 py-1.5 text-xs bg-slate-700 border border-slate-600 rounded text-white placeholder-slate-400"
          placeholder="Possible issue..."
          data-testid="label-text-input"
        />
      </div>

      {/* Font Preset */}
      <div>
        <label className="text-xs text-slate-500 block mb-1">Font Style</label>
        <select
          value={fontPreset}
          onChange={(e) => setFontPreset(e.target.value)}
          className="w-full px-2 py-1.5 text-xs bg-slate-700 border border-slate-600 rounded text-white"
          data-testid="font-preset-select"
        >
          {Object.values(FONT_PRESETS).map(fp => (
            <option key={fp.id} value={fp.id}>{fp.label}</option>
          ))}
        </select>
      </div>

      {/* Law Tags */}
      <div>
        <label className="text-xs text-slate-500 block mb-1">Law Tags (comma separated)</label>
        <input
          type="text"
          value={lawTags}
          onChange={(e) => setLawTags(e.target.value)}
          className="w-full px-2 py-1.5 text-xs bg-slate-700 border border-slate-600 rounded text-white placeholder-slate-400"
          placeholder="FDCPA 1692g, FDCPA 1692e"
          data-testid="law-tags-input"
        />
      </div>

      {/* Action Buttons */}
      <div className="flex gap-2 pt-1">
        <Button
          variant="default"
          size="sm"
          onClick={handleSave}
          className="flex-1 h-7 text-xs bg-emerald-600 hover:bg-emerald-700"
          data-testid="save-annotation-btn"
        >
          <Check className="w-3 h-3 mr-1" />
          Save
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={onDelete}
          className="h-7 text-xs border-red-500 text-red-400 hover:bg-red-500/10"
          data-testid="delete-annotation-btn"
        >
          <Trash2 className="w-3 h-3" />
        </Button>
      </div>

      {/* Detachment Warning */}
      {annotation.manually_detached && (
        <div className="p-2 bg-amber-500/10 border border-amber-500/30 rounded text-xs text-amber-400">
          ⚠️ This annotation has been moved away from its original AI-anchored position.
        </div>
      )}
    </div>
  );
};

export default MarkupEditorToolbar;
